import React, { useEffect, useState } from 'react';

import './LandingQuickNav.css';

/*
 * Quick section tickers — a slim vertical rail that appears once the
 * visitor scrolls past the hero and lets them jump-scroll between the
 * landing sections. The active chip tracks the section in view.
 * Progressive enhancement only: without JavaScript it simply never
 * appears (the page still has the persona tiles for navigation).
 */

const SECTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'projects', label: 'Projects' },
  { id: 'personas', label: 'Who it’s for' },
  { id: 'platform', label: 'Features' },
  { id: 'process', label: 'How it works' },
  { id: 'collaboration', label: 'Humans + AI' },
  { id: 'moat', label: 'Why 8080' },
  { id: 'trust', label: 'Trust' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'contact', label: 'Contact' },
];

const useQuickNav = () => {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => {
      setVisible(globalThis.scrollY > 560);

      const pageHeight = globalThis.document.documentElement.scrollHeight;
      const atPageEnd = globalThis.scrollY + globalThis.innerHeight >= pageHeight - 24;

      if (atPageEnd) setActive('contact');
    };

    globalThis.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const io = new globalThis.IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActive(e.target.id);
      });
    }, { rootMargin: '-20% 0px -70% 0px' });

    SECTIONS.forEach((s) => {
      const el = globalThis.document.getElementById(s.id);

      if (el) io.observe(el);
    });

    return () => {
      globalThis.removeEventListener('scroll', onScroll);
      io.disconnect();
    };
  }, []);

  return { visible, active };
};

const LandingQuickNavView: React.FC<{ isAuthenticated?: boolean }> = ({ isAuthenticated }) => {
  const { visible, active } = useQuickNav();

  return (
    <nav className={`lq${visible ? ' lq--on' : ''}`} aria-label="Quick section navigation">
      {SECTIONS.map((s) => (
        <a
          key={s.id}
          className={`lq__chip${active === s.id ? ' lq__chip--on' : ''}`}
          href={`#${s.id}`}
          aria-current={active === s.id ? 'location' : undefined}
          title={s.id === 'projects' && isAuthenticated ? 'My projects' : s.id === 'projects' ? 'Public projects' : s.label}
        >
          <span className="lq__label">
            {s.id === 'projects' && (isAuthenticated ? 'My projects' : 'Public projects')}
            {s.id !== 'projects' && s.label}
          </span>
        </a>
      ))}
    </nav>
  );
};

/* Scroll-driven, so it re-renders on its own schedule; memoised so it does not
   also re-render on every keystroke in the landing chat input. */
export const LandingQuickNav = React.memo(LandingQuickNavView);
LandingQuickNav.displayName = 'LandingQuickNav';
